import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import axios, { CancelToken } from "axios";
import moment from "moment";

import { StatusCode } from "api/protocols";
import { GetLeague, ILeaguePut, PutLeague } from "api/rpc/2024-04/facilityAdmin/league/league";
import { GetLeagueRegistrationFees } from "api/rpc/2024-04/facilityAdmin/league/registration/registration";

import { showError, showSuccess } from "redux/actions/ui";
import { ILeague, ILeagueRegistrationFee } from "redux/reducers/models/league";
import { useAppDispatch, useAppSelector } from "hooks/redux";

import Toggle from "components/form/toggle/Toggle";
import Page from "components/page/Page";
import Tabs from "components/tabs/Tabs";
import Input from "components/form/input/Input";
import FormLayout from "components/form/FormLayout";
import RegistrationFeeTab from "./tabs/fee/RegistrationFeeTab";
import { handleChangeEventInput } from "helpers/Helpers";
import { ButtonNew as Button } from "components/buttonNew";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import TimePick from "components/timePick/TimePick";
import Card from "components/card/Card";
import CopyLink from "components/copyLink/CopyLink";

import "./leagueRegistrationLanding.scss";
import useRegisterDates, { IRegisterDate } from "hooks/useDateTime/useDateTime";

interface ILeagueRegistrationAmountRange {
  min: number;
  max: number;
}

export default function LeagueRegistrationLanding() {
  const { leagueId } = useParams<{ leagueId: string }>();
  const { t } = useTranslation();
  const { facilityStore } = useAppSelector(store => store);

  const dispatch = useAppDispatch();

  const [tabIndex, setTabIndex] = useState(0);
  const [leagueDetails, setLeagueDetails] = useState<ILeague>(undefined);

  const {
    registerOpen,
    registerClose,
    registerDateRangeValid,
    registerTimeRangeValid,
    updateRegisterOpen,
    updateRegisterClose,
  } = useRegisterDates();

  const [registrationFee, setRegistrationFee] = useState({
    loadTrigger: false,
    fees: undefined as ILeagueRegistrationFee[],
    addons: undefined as ILeagueRegistrationFee[],
  });

  const [link, setLink] = useState({
    shortName: "",
    handle: "",
    registerLink: "",
    leaderboardLink: "",
  });

  const [registrationAmountRange, setRegistrationAmountRange] = useState<ILeagueRegistrationAmountRange>({
    min: null,
    max: null,
  });

  const pageTabs = [
    {
      id: "league_registration-general",
      content: "General",
      disabled: false,
    },
    {
      id: "league_registration-fees",
      content: "Fees",
      disabled: !leagueDetails?.registration_open,
    },
  ];

  useEffect(() => {
    const source = axios.CancelToken.source();
    if (facilityStore.facility) {
      void loadLeague(leagueId, source.token);
    }

    return () => {
      source.cancel();
    };
  }, [leagueId, facilityStore]);

  useEffect(() => {
    setLink(prevState => ({ ...prevState, shortName: facilityStore?.facility?.short_name }));
  }, [facilityStore.facility?.short_name]);

  // define registration fee state only when toggled
  useEffect(() => {
    const source = axios.CancelToken.source();
    if (registrationFee.loadTrigger) {
      void loadLeagueRegistrationFees(leagueId, source.token);
    }
    return () => {
      source.cancel();
    };
  }, [registrationFee.loadTrigger, leagueId]);

  function getRegisterDateFromDateString(dateString: string, timezone: string): IRegisterDate {
    if (dateString) {
      const facilityDate = moment.utc(dateString).tz(timezone);

      const date = new Date(facilityDate.year(), facilityDate.month(), facilityDate.date());

      const time = facilityDate.format("HH:mm");

      return {
        date,
        time,
      };
    } else {
      return null;
    }
  }

  // It is assumed the id will always return a value
  async function loadLeague(id: string | number, token?: CancelToken) {
    const res = await GetLeague({ id: id }, token ? false : true, token);
    if (token && token.reason) {
      return;
    }

    if (res.status !== StatusCode.OK || res.data.length === 0) {
      return;
    }

    setLeagueDetails(res.data[0]);

    setLink(prevState => ({
      ...prevState,
      handle: res?.data[0]?.handle,
    }));

    setRegistrationAmountRange(prev => ({
      ...prev,
      min: res.data?.[0]?.minimum_registration_amount,
      max: res.data?.[0]?.maximum_registration_amount,
    }));

    const updatedRegisterOpen = getRegisterDateFromDateString(
      res.data[0]?.registration_open_date,
      facilityStore.facility?.timezone,
    );
    updateRegisterOpen(updatedRegisterOpen);

    const updatedRegisterClose = getRegisterDateFromDateString(
      res.data[0]?.registration_close_date,
      facilityStore.facility?.timezone,
    );
    updateRegisterClose(updatedRegisterClose);

    if (res.data[0].registration_open) {
      setRegistrationFee(prev => ({ ...prev, loadTrigger: true }));
    }
  }

  /** Passed to RegistrationFeeTab */
  async function loadLeagueRegistrationFees(id: string | number, token?: CancelToken) {
    if (registrationFee.fees !== undefined) {
      setRegistrationFee(prev => ({ ...prev, fees: undefined }));
    }

    if (registrationFee.addons !== undefined) {
      setRegistrationFee(prev => ({ ...prev, addons: undefined }));
    }

    const res = await GetLeagueRegistrationFees({ league_id: id }, token ? false : true, token);

    if (token && token.reason) {
      return;
    }

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.league.fees.002")));
    }

    setRegistrationFee(prev => ({
      ...prev,
      loadTrigger: false,
      fees: res.status !== StatusCode.OK ? [] : res.data.filter((fee: any) => fee.type === "registration_fee"),
      addons: res.status !== StatusCode.OK ? [] : res.data.filter((fee: any) => fee.type === "add_on"),
    }));
  }

  /**
   * Handles Toggle element state before and after PUTLeague call
   * - PUTLeague registration_open + GETLeagueRegistrationFees trigger
   * */
  async function toggleLeagueRegistration(toggled: boolean) {
    const previousOnlineStore = leagueDetails?.online_store;
    setLeagueDetails(prev => ({ ...prev, registration_open: toggled, online_store: toggled ? prev.online_store : false }));
    const res = await PutLeague(
      { id: leagueId, registration_open: toggled, ...(toggled ? {} : { online_store: false }) },
      true,
    );

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error enabling registration.")));
      setLeagueDetails(prev => ({ ...prev, registration_open: !toggled, online_store: previousOnlineStore }));
      return;
    }

    // allow CancelToken with call
    setRegistrationFee(prev => ({ ...prev, loadTrigger: true }));
  }

  async function toggleLeagueOnlineStore(toggled: boolean) {
    if (!leagueDetails?.registration_open) {
      return;
    }

    setLeagueDetails(prev => ({ ...prev, online_store: toggled }));
    const res = await PutLeague({ id: leagueId, online_store: toggled }, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error updating online store.")));
      setLeagueDetails(prev => ({ ...prev, online_store: !toggled }));
    }
  }

  // Strictly makes PUTLeague call
  async function updateLeague(args: Omit<ILeaguePut, "id">) {
    const registrationOpenTime = registerOpen?.time?.split(":");

    const registrationOpenDate =
      !registerOpen?.date || !registerOpen?.time
        ? null
        : moment
            .tz(
              [
                registerOpen.date.getFullYear(),
                registerOpen.date.getMonth(),
                registerOpen.date.getDate(),
                Number(registrationOpenTime?.[0]),
                Number(registrationOpenTime?.[1]),
              ],
              facilityStore.facility?.timezone,
            )
            .utc()
            .format("YYYY-MM-DD HH:mm:ss");

    const registrationCloseTime = registerClose?.time?.split(":");

    const registrationCloseDate =
      !registerClose?.date || !registerClose?.time
        ? null
        : moment
            .tz(
              [
                registerClose.date.getFullYear(),
                registerClose.date.getMonth(),
                registerClose.date.getDate(),
                Number(registrationCloseTime?.[0]),
                Number(registrationCloseTime?.[1]),
              ],
              facilityStore.facility?.timezone,
            )
            .utc()
            .format("YYYY-MM-DD HH:mm:ss");

    const params: ILeaguePut = {
      id: leagueId,
      registration_open_date: registrationOpenDate,
      registration_close_date: registrationCloseDate,
      minimum_registration_amount: registrationAmountRange.min,
      maximum_registration_amount: registrationAmountRange.max,
      ...args,
    };

    const res = await PutLeague(params, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(typeof res.data === "string" ? res.data : t("secure.facility.league.league_settings.003")));
      return;
    }

    dispatch(showSuccess(t("secure.facility.league.league_settings.004")));
    setLeagueDetails(prev => ({ ...prev, ...res?.data }));
  }

  // When player_limit is empty, remove waitlist_limit value
  function handlePlayerLimitChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.value && e.target.value == "0" && leagueDetails.waitlist_limit) {
      setLeagueDetails(prev => ({ ...prev, waitlist_limit: null }));
    }
    handleChangeEventInput(e, leagueDetails, setLeagueDetails);
  }

  function handleRegistrationAmountChange(e: React.ChangeEvent<HTMLInputElement>) {
    handleChangeEventInput(e, registrationAmountRange, setRegistrationAmountRange);
  }

  function toggleRegisterOpen(checked: boolean) {
    if (!checked) {
      updateRegisterOpen(null);
    } else {
      if (!leagueDetails?.registration_open_date) {
        updateRegisterOpen({
          date: new Date(),
          time: "08:00",
        });
      } else {
        const updatedRegisterOpen = getRegisterDateFromDateString(
          leagueDetails?.registration_open_date,
          facilityStore.facility?.timezone,
        );
        updateRegisterOpen(updatedRegisterOpen);
      }
    }
  }

  function toggleRegisterClose(checked: boolean) {
    if (!checked) {
      updateRegisterClose(null);
    } else {
      if (!leagueDetails?.registration_close_date) {
        updateRegisterClose({
          date: new Date(),
          time: "20:00",
        });
      } else {
        const updatedRegisterClose = getRegisterDateFromDateString(
          leagueDetails?.registration_close_date,
          facilityStore.facility?.timezone,
        );
        updateRegisterClose(updatedRegisterClose);
      }
    }
  }

  function toggleRegistrationAmountRangeMinimum(checked: boolean) {
    if (!checked) {
      setRegistrationAmountRange(prev => ({ ...prev, min: null }));
    } else {
      setRegistrationAmountRange(prev => ({ ...prev, min: leagueDetails.minimum_registration_amount ?? 2 }));
    }
  }

  function toggleRegistrationAmountRangeMaximum(checked: boolean) {
    if (!checked) {
      setRegistrationAmountRange(prev => ({ ...prev, max: null }));
    } else {
      setRegistrationAmountRange(prev => ({ ...prev, max: leagueDetails.maximum_registration_amount ?? 4 }));
    }
  }

  return (
    <Page title={t("Registration")} className="league_registration-page">
      {tabIndex === 0 && (
        <div className="league_registration-save_btn">
          <Button
            type="primary"
            disabled={
              !registerDateRangeValid ||
              !registerTimeRangeValid ||
              registrationAmountRange.min < 0 ||
              registrationAmountRange.max < 0 ||
              (registrationAmountRange.min != null &&
                registrationAmountRange.max != null &&
                registrationAmountRange.min > registrationAmountRange.max)
            }
            onClick={() =>
              updateLeague({
                player_limit: leagueDetails.player_limit,
                waitlist_limit: leagueDetails.waitlist_limit,
              })
            }
          >
            {t("Save")}
          </Button>
        </div>
      )}
      <Tabs tabs={pageTabs} selected={tabIndex} onSelect={index => setTabIndex(index)}>
        {tabIndex === 0 && (
          <>
            <Card style={{ marginTop: "8px" }}>
              <Card.Section title={t("General Settings")}>
                <FormLayout.Group>
                  <div className="league_registration-toggle">
                    <Toggle
                      id="registration_open"
                      labelRight="Enable Registration"
                      checked={leagueDetails ? leagueDetails.registration_open : false}
                      onChange={e => toggleLeagueRegistration(e.target.checked)}
                    />
                    <Toggle
                      id="online_store"
                      labelRight={t("Add to Online Store")}
                      checked={!!leagueDetails?.online_store}
                      disabled={!leagueDetails?.registration_open}
                      onChange={e => toggleLeagueOnlineStore(e.target.checked)}
                    />
                  </div>
                  {leagueDetails?.registration_open ? (
                    <div className="league_registration-limit_inputs">
                      <Input
                        id="player_limit"
                        value={!leagueDetails.player_limit ? 0 : leagueDetails.player_limit}
                        type="number"
                        onChange={handlePlayerLimitChange}
                        label={t("Player Limit")}
                      />
                      <div>
                        {!!leagueDetails.player_limit && leagueDetails.player_limit !== 0 ? (
                          <Input
                            id="waitlist_limit"
                            value={!leagueDetails.waitlist_limit ? 0 : leagueDetails.waitlist_limit}
                            type="number"
                            onChange={e => handleChangeEventInput(e, leagueDetails, setLeagueDetails)}
                            label={t("Waitlist Limit")}
                          />
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                </FormLayout.Group>
              </Card.Section>
            </Card>
            <Card>
              <Card.Section title={t("Registration Amount Range")}>
                <div className="mt-3">
                  <FormLayout.Group>
                    <Toggle
                      labelRight="Set Minimum"
                      checked={registrationAmountRange.min != null}
                      onChange={e => toggleRegistrationAmountRangeMinimum(e.target.checked)}
                    />
                  </FormLayout.Group>
                  <FormLayout.Group>
                    {registrationAmountRange.min != null && (
                      <div className="league_registration-amount-range-input-container">
                        <Input
                          id="min"
                          value={!registrationAmountRange.min ? 0 : registrationAmountRange.min}
                          type="number"
                          onChange={handleRegistrationAmountChange}
                          label={t("Minimum")}
                        />
                      </div>
                    )}
                  </FormLayout.Group>
                  <FormLayout.Group>
                    <Toggle
                      labelRight="Set Maximum"
                      checked={registrationAmountRange.max != null}
                      onChange={e => toggleRegistrationAmountRangeMaximum(e.target.checked)}
                    />
                  </FormLayout.Group>
                  <FormLayout.Group>
                    {registrationAmountRange.max != null && (
                      <div className="league_registration-amount-range-input-container">
                        <Input
                          id="max"
                          value={!registrationAmountRange.max ? 0 : registrationAmountRange.max}
                          type="number"
                          onChange={handleRegistrationAmountChange}
                          label={t("Maximum")}
                        />
                      </div>
                    )}
                  </FormLayout.Group>
                </div>
              </Card.Section>
            </Card>
            <Card>
              <Card.Section title={t("Registration Dates")}>
                <div className="mt-3">
                  <FormLayout.Group>
                    <Toggle
                      labelRight="Set Open Date"
                      checked={!!registerOpen?.date}
                      onChange={e => toggleRegisterOpen(e.target.checked)}
                    />
                  </FormLayout.Group>
                  <FormLayout.Group>
                    {registerOpen && (
                      <div className="league_registration-date-inputs">
                        <div style={{ flex: 1 }}>
                          <DatePickerInput
                            months={1}
                            position={"left"}
                            label={t("Open Date")}
                            startingDate={registerOpen?.date}
                            setStartingDate={selectedDate =>
                              updateRegisterOpen({ date: selectedDate as Date, time: registerOpen?.time })
                            }
                            error={!registerDateRangeValid}
                            showBlurTitleOnError
                          />
                        </div>

                        <div
                          style={{
                            display: "flex",
                            flex: 1,
                            alignItems: "center",
                          }}
                        >
                          <TimePick
                            value={registerOpen?.time}
                            onChange={timeString => updateRegisterOpen({ date: registerOpen?.date, time: timeString })}
                            align="right"
                            size="large"
                            label={t("Open Time")}
                            status={!registerTimeRangeValid ? "error" : undefined}
                          />
                        </div>
                      </div>
                    )}
                  </FormLayout.Group>
                  <FormLayout.Group>
                    <Toggle
                      labelRight="Set Close Date"
                      checked={!!registerClose?.date}
                      onChange={e => toggleRegisterClose(e.target.checked)}
                    />
                  </FormLayout.Group>
                  <FormLayout.Group>
                    {registerClose && (
                      <div className="league_registration-date-inputs">
                        <div style={{ flex: 1 }}>
                          <DatePickerInput
                            months={1}
                            position={"left"}
                            label={t("Close Date")}
                            startingDate={registerClose?.date}
                            setStartingDate={selectedDate =>
                              updateRegisterClose({ date: selectedDate as Date, time: registerClose?.time })
                            }
                            error={!registerDateRangeValid}
                            showBlurTitleOnError
                          />
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flex: 1,
                            alignItems: "center",
                          }}
                        >
                          <TimePick
                            value={registerClose?.time}
                            onChange={timeString =>
                              updateRegisterClose({ date: registerClose?.date, time: timeString })
                            }
                            align="right"
                            size="large"
                            label={t("Close Time")}
                            status={!registerTimeRangeValid ? "error" : undefined}
                          />
                        </div>
                      </div>
                    )}
                  </FormLayout.Group>
                </div>
              </Card.Section>
            </Card>

            <Card title={t("Links")}>
              <Card.Section>
                <CopyLink
                  label={t("Registration")}
                  link={window.origin + `/league/${link?.shortName}/${link?.handle}/register`}
                />
              </Card.Section>
              {leagueDetails?.online_store && (
              <Card.Section>
                <CopyLink
                    label={t("Online Store")}
                    link={
                      window.origin +
                      "/online-store/" +
                      facilityStore?.facility?.short_name +
                      "?groupHandle=events"
                    }
                  />
              </Card.Section>
              )}
            </Card>
          </>
        )}

        {tabIndex === 1 && (
          <RegistrationFeeTab
            leagueId={parseInt(leagueId)}
            fees={registrationFee.fees}
            addons={registrationFee.addons}
            setUpdatedFees={(fees, trigger) =>
              setRegistrationFee(prev => ({ ...prev, fees: fees, loadTrigger: trigger }))
            }
            setUpdatedAddons={(addons, trigger) =>
              setRegistrationFee(prev => ({ ...prev, addons: addons, loadTrigger: trigger }))
            }
          />
        )}
      </Tabs>
    </Page>
  );
}
